import { HTMLAttributes, memo, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { Badge, Button, Modal, Popover, Space } from 'antd';
import Icon, { DeleteOutlined, FormOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { ReactComponent as iconMore } from 'views/Setting/BlockList/assets/more.svg';
import StoreRow from 'bizComponents/StoreRow';

const Section = styled.section`
  background: #fff;
  border: solid 1px #e1e4eb;
  > header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 56px;
    padding: 0 15px;
    border-bottom: solid 1px #e1e4eb;
  }
  > div {
    display: flex;
    justify-content: space-between;
    > section {
      flex: 1 0 0;
      padding: 10px 15px;
      overflow: hidden;
      > h5 {
        font-size: 14px;
        color: #84868c;
        button {
          padding: 0;
          color: #84868c;
          &.active {
            color: #336fff;
          }
        }
        > span {
          margin: 0 10px;
          font-size: 12px;
          vertical-align: baseline;
        }
      }
      > div {
        > span {
          display: inline-block;
        }
      }
    }
    > section + section {
      border-left: solid 1px #e1e4eb;
    }
  }
`;

const Profile = styled.div`
  display: flex;
  align-items: center;
  > * {
    margin-right: 16px;
  }
  * {
    font-size: 12px;
  }
  > h4 {
    font-size: 16px;
    margin-bottom: 0;
    margin-right: 4px;
  }
  > div {
    display: flex;
    align-items: center;
    height: 24px;
    padding: 0 8px;
    background: #f0f2f5;
    border-radius: 2px;
    cursor: pointer;
  }
  .anticon {
    margin-left: 4px;
    cursor: pointer;
  }
`;

const PopoverContent = styled.div`
  width: 210px;
  > ul {
    margin-bottom: 0;
    padding-left: 15px;
  }
`;

const Tag = styled.span`
  display: inline-block;
  height: 20px;
  line-height: 18px;
  font-size: 12px;
  padding: 0 8px;
  border-radius: 2px;
  background: #f0f2f5;
  color: #84868c;
  & + & {
    margin-left: 8px;
  }
`;

export const StoreTag = styled(Tag)<{ platform: General.PlatformUpperCase }>`
  color: ${({ platform }) => {
    return {
      SHOPEE: '#F15D5D',
      LAZADA: '#3D76FE',
      TOKOPEDIA: '#3D76FE',
      TIKTOK: '#3f403f',
      TIKTOK_US: '#3f403f',
      ALIEXPRESS: '#3D76FE',
      MERCADO: '#3D76FE',
      KWAI: '#3D76FE',
      FACEBOOK: '#3D76FE',
    }[platform];
  }};
  background: ${({ platform }) => {
    return {
      SHOPEE: '#FAE6E6',
      LAZADA: '#D7E1FA',
      TOKOPEDIA: '#D7E1FA',
      TIKTOK: '#D7E1FA',
      TIKTOK_US: '#D7E1FA',
      ALIEXPRESS: '#D7E1FA',
      MERCADO: '#D7E1FA',
      KWAI: '#D7E1FA',
      FACEBOOK: '#D7E1FA',
    }[platform];
  }};
  border: none;
  vertical-align: middle;
  cursor: pointer;
`;

const DeleteButton = styled(Button)`
  color: #94959a;
`;
const More = styled.span`
  color: #84868c;
  font-size: 12px;
  display: flex;
  gap: 6px;
  align-items: center;
  margin-top: 10px;
  width: fit-content;
  .anticon {
    margin: 0;
  }
`;
const MoreStorePopContent = styled.div`
  max-height: 280px;
  overflow: auto;
`;

export interface GroupComponentProps extends HTMLAttributes<HTMLDivElement> {
  group: Assistant.GroupWithEnableInfo;
  onEdit: (group: Assistant.Group) => void;
  onDelete: (group: Assistant.Group) => Promise<any>;
}

function GroupComponent(props: GroupComponentProps) {
  const { group, onEdit, onDelete, ...otherProps } = props;

  const {
    enableUserNum,
    disableUserNum,
    sleepUserNum,
    assistantGroup,
    assistantGroup: {
      name,
      users,
      stores,
      receptionScope,
      tradeStatusFilters,
      buyerTag,
      assistantGroupTunnelAccount,
    },
  } = group;

  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<'pool' | 'store'>(
    !stores.length && assistantGroupTunnelAccount?.length ? 'pool' : 'store',
  );

  const { t } = useTranslate();
  const storeList = stores.slice(0, 3);
  const moreStore = stores.slice(3);
  const poolList = assistantGroupTunnelAccount?.slice(0, 3) || [];
  const morePool = assistantGroupTunnelAccount?.slice(3) || [];
  return (
    <Section {...otherProps}>
      <header>
        <Profile>
          <h4>{name}</h4>
          {useMemo(() => {
            if (enableUserNum <= 0) {
              return null;
            }

            return (
              <Popover
                placement="top"
                content={
                  <PopoverContent>
                    <ul>
                      <li>
                        {t('group.当前客服组中仅有1位启用中的客服买家将会全部分配给该客服处理')}
                      </li>
                      <li>{t('group.当前存在多位启用中客服时，买家将会平均分配')}</li>
                    </ul>
                  </PopoverContent>
                }
              >
                <div>
                  {enableUserNum === 1 ? t('group.全部分配') : t('group.平均分配')}
                  <InfoCircleOutlined />
                </div>
              </Popover>
            );
          }, [enableUserNum, t])}

          <Badge status="success" text={t('group.enableCount', { count: enableUserNum })} />
          <span>
            <Badge status="warning" text={t('group.sleepCount', { count: sleepUserNum })} />
            <Popover
              placement="top"
              content={
                <PopoverContent>
                  {t(
                    'group.连续7天未登录系统的客服将进入休眠状态，不再进行买家分配，再次登录则恢复买家分配',
                  )}
                </PopoverContent>
              }
            >
              <InfoCircleOutlined />
            </Popover>
          </span>
          <Badge status="default" text={t('group.disableCount', { count: disableUserNum })} />
        </Profile>
        <div>
          <DeleteButton type="link" onClick={() => onEdit(assistantGroup)} icon={<FormOutlined />}>
            {t('group.edit')}
          </DeleteButton>
          <DeleteButton
            loading={deleting}
            type="link"
            icon={<DeleteOutlined />}
            onClick={() => {
              const { destroy } = Modal.confirm({
                title: t('group.deleteConfirm'),
                onOk: () => {
                  destroy();

                  setDeleting(true);

                  onDelete(assistantGroup).finally(() => {
                    setDeleting(false);
                  });
                },
              });
            }}
          >
            {t('group.delete')}
          </DeleteButton>
        </div>
      </header>
      <div>
        <section>
          <h5 data-guide="step-2">{t('group.accountCount')}</h5>
          <div>
            {users.length
              ? users
                  .map((account) => {
                    const { name } = account;

                    return name;
                  })
                  .join(' | ')
              : t('group.当前组内无客服，买家将无法分配至该客服组')}
          </div>
        </section>
        <section>
          <h5 data-guide="step-3">
            <Space size={4}>
              {storeList.length ? (
                <Button
                  type="link"
                  className={activeTab === 'store' ? 'active' : ''}
                  onClick={() => setActiveTab('store')}
                >
                  {t('group.负责店铺')}
                </Button>
              ) : null}

              {storeList.length &&
              assistantGroupTunnelAccount?.filter((v) => v.tunnelType === 'WA_BIZ')?.length ? (
                <>
                  {' | '}
                  <Button
                    type="link"
                    className={activeTab === 'pool' ? 'active' : ''}
                    onClick={() => setActiveTab('pool')}
                  >
                    {t('group.负责WABA帐号')}
                  </Button>
                </>
              ) : null}
            </Space>
          </h5>
          {activeTab === 'store' ? (
            <>
              {storeList.map((store) => (
                <StoreRow store={store} key={store.storeId} />
              ))}
              {moreStore.length > 0 && (
                <Popover
                  content={
                    <MoreStorePopContent>
                      {moreStore.map((store) => (
                        <StoreRow store={store} key={store.storeId} />
                      ))}
                    </MoreStorePopContent>
                  }
                >
                  <More>
                    {t('group.更多x家店铺', { total: moreStore.length })}{' '}
                    <Icon component={iconMore} />
                  </More>
                </Popover>
              )}
            </>
          ) : (
            <>
              {poolList?.map((store) => (
                <div key={store.tunnelAccountId}>{store.tunnelAccountName}</div>
              ))}
              {morePool.length > 0 && (
                <Popover
                  content={
                    <MoreStorePopContent>
                      {morePool.map((store) => (
                        <div key={store.tunnelAccountId}>{store.tunnelAccountName}</div>
                      ))}
                    </MoreStorePopContent>
                  }
                >
                  <More>
                    {t('group.更多x帐号', { x: morePool.length })} <Icon component={iconMore} />
                  </More>
                </Popover>
              )}
            </>
          )}
        </section>
        <section>
          <h5>{t('field.receptionScope')}</h5>
          <div>
            {useMemo(() => {
              if (receptionScope === 'ALL_BUYER') {
                return <Tag>{t(`receptionScopeType.${receptionScope}`)}</Tag>;
              }

              if (receptionScope === 'TRADE_STATUS' && tradeStatusFilters) {
                return tradeStatusFilters.map((tradeStatus) => {
                  return <Tag key={tradeStatus}>{t(`tradeStatus.${tradeStatus}`)}</Tag>;
                });
              }

              if (receptionScope === 'BUYER_LABEL' && buyerTag) {
                return buyerTag.map((tag) => {
                  return <Tag key={tag}>{t(`buyerTag.${tag}`)}</Tag>;
                });
              }

              return null;
            }, [receptionScope, tradeStatusFilters, buyerTag, t])}
          </div>
        </section>
      </div>
    </Section>
  );
}

export default memo(GroupComponent);
